/*
 * Copyright (c) 2022, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useMemo, useRef, useState} from 'react'
import PropTypes from 'prop-types'

// Components
import {
    AspectRatio,
    Box,
    Skeleton as ChakraSkeleton,
    Text,
    Stack,
    useMultiStyleConfig,
    Button,
    useMediaQuery,
    Img,
    UnorderedList,
    ListItem,
    Divider,
    Fade,
    Flex,
} from '@chakra-ui/react'
import Checkbox from '../checkbox'

// Project Components

import Link from '@salesforce/retail-react-app/app/components/link'
import Swatch from '../swatch-group/swatch'
// import PromoCallout from '@salesforce/retail-react-app/app/components/product-tile/promo-callout'

// Hooks
import {FormattedMessage, useIntl} from 'react-intl'

// Other
import {PRODUCT_BADGE_DETAILS} from '@salesforce/retail-react-app/app/constants'
import {useCompare} from '../../context/compare'
import {breakpoints, remCalc} from '../../theme/_utils'
import EnergyLabel from '../energy-label'
import StockLabel from '../stock-label'
import CloseIcon from '../icons/action/CloseIcon'
import ProductTileImage from './partials/product-tile-image'
import AddToCartButton from '../add-to-cart-button'
import NotifyMeButton from '../notify-me-button'
import {
    ADD_TO_CART_ERROR_KEYS,
    ADD_TO_CART_ERROR_MESSAGES,
    PRODUCT_CUSTOM_SELECTABLE_ATTRIBUTE_ID,
} from '../../constants'
import {InfoIcon} from '../icons/others'
import {filterImageGroups, getPriceData} from '../../utils/product-utils'
import useProductData from '../../hooks/use-product-data'
import DisplayPrice from '../display-price'
import OmnibusPrice from '../display-price/omnibus-price'
import VatLabel from '../vat-label'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'

import loadable from '@loadable/component'
const ReviewScoreIcon = loadable(() => import('../review/ReviewScoreIcon'))

import {useBooleanCustomPreference, useCustomPreference} from '../../hooks/use-custom-preference'
import {productUrlBuilder, rebuildPathWithParams} from '../../utils/url'
import {parseAndSanitizeJSONWithSpaces} from '../../utils/utils'
import {useGoogleTagManager} from '../../custom-api/hooks/use-google-tag-manager'

import {
    basic_gtm_event,
    item_gtm_event,
    polish_ecommerce_gtm_event,
} from '../../custom-api/layers/default'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'

// Component Skeleton
export const Skeleton = () => {
    const styles = useMultiStyleConfig('ProductTile')

    return (
        <Box data-testid="sf-product-tile-skeleton">
            <Stack spacing={2}>
                <Box {...styles.imageWrapper}>
                    <AspectRatio ratio={1} {...styles.image}>
                        <ChakraSkeleton />
                    </AspectRatio>
                </Box>
                <ChakraSkeleton width="80px" height="20px" />
                <ChakraSkeleton width={{base: '120px', md: '220px'}} height="12px" />
            </Stack>
        </Box>
    )
}

/**
 * The ProductTile is a simple visual representation of a
 * product object. It will show it's default image, name and price.
 * It also supports favourite products, controlled by a heart icon.
 */
const ProductTile = ({
    dynamicImageProps,
    enableFavourite = false,
    isFavourite,
    onFavouriteToggle,
    product,
    productIndex = undefined,
    gridView = false,
    shouldHideReviews = false,
    shouldHideVariants = false,
    shouldHideCompare = false,
    selectableAttributeId = PRODUCT_CUSTOM_SELECTABLE_ATTRIBUTE_ID,
    badgeDetails = PRODUCT_BADGE_DETAILS,
    categoryName = '',
    ...rest
}) => {
    const {imageGroups} = product
    const [isDesktop] = useMediaQuery(`(min-width: ${breakpoints.lg})`)
    const [isMobile] = useMediaQuery(`(max-width: ${breakpoints.md})`)
    const notShoppableCountry = useBooleanCustomPreference('notShoppableCountry')
    const [isHovered, setIsHovered] = useState(false)
    const compactView = gridView && isMobile
    const {addLayer} = useGoogleTagManager()

    const {locale} = useMultiSite()
    const localeId = locale?.alias

    const {currency} = useCurrency()

    const styles = useMultiStyleConfig('ProductTile', {
        variant: isDesktop ? 'collapsable' : compactView ? 'compact' : 'default',
        isHovered,
        shouldHideVariants,
    })

    // ProductTile is used by two components, RecommendedProducts and ProductList.
    // RecommendedProducts provides a localized product name as `name` and non-localized product
    // name as `productName`. ProductList provides a localized name as `productName` and does not
    // use the `name` property.
    const localizedProductName = product.name ?? product.productName

    const {productUrl, priceData} = useProductData(product, selectableAttributeId)

    const keyFeaturesTitles = (
        parseAndSanitizeJSONWithSpaces(
            product?.representedProduct?.c_keyFeature,
            product?.representedProduct?.c_subModelIdentifier,
        ) || []
    ).map((item) => item.title)

    const {
        state: {compareItems},
        addItem,
        removeItem,
    } = useCompare()

    const onCheckboxChange = (product) => {
        if (compareItems.includes(product)) {
            removeItem(product)
        } else {
            addItem(product)
        }
    }

    const isChecked = compareItems.includes(product)

    const colorVariantsWithImages = useMemo(() => {
        return product?.c_swatches
            ? product?.c_swatches
            : product?.variationGroups
                  ?.filter((variation) => variation.productId !== product.productId)
                  .map((group) => {
                      const filteredImages = filterImageGroups(imageGroups, {
                          viewType: 'swatch',
                          variationValues: group.variationValues,
                      })
                      return {
                          ...group,
                          images: filteredImages[0]?.images,
                      }
                  })
    }, [product])

    const labelElement = colorVariantsWithImages?.length > 0 && (
        <Text {...styles.variantsLabel}>
            {colorVariantsWithImages?.length}{' '}
            <FormattedMessage
                id="product_tile.other_colours"
                defaultMessage="other colours available"
            />
        </Text>
    )

    const [showErrorMessage, setShowErrorMessage] = useState(0)
    const errorContainerRef = useRef(null)
    const {formatMessage} = useIntl()

    const lastItemThreshold = useCustomPreference('showLastItemThreshold')
    const isVATFlag = useBooleanCustomPreference('showVATFlag')

    const selectItemTileEventGtm = async () => {
        if (shouldHideReviews && shouldHideVariants && shouldHideCompare) {
            await addLayer(
                basic_gtm_event.bind(this, {
                    event: 'help_me_choose',
                    eventCategory: 'engagement',
                    eventAction: 'help_me_choose-product_click',
                    eventLabel: product.id,
                }),
            )
        }
        await addLayer(polish_ecommerce_gtm_event.bind(this))
        await addLayer(
            item_gtm_event.bind(this, {
                event: 'select_item',
                item_list_name: categoryName,
                items: [
                    {
                        item_name: product?.c_defaultName,
                        item_id: product?.representedProduct?.id,
                        item_category: product?.c_defaultCategoryName,
                        item_variant: product?.productId,
                        price: +(product?.price / (1 + product?.c_taxRate / 100)).toFixed(2),
                        fullprice: getPriceData(product)?.isOnSale ? 'reduced' : 'fullprice',
                        rating_points: '',
                        // get_reevoo_rating() || get_bv_rating(),
                        erp_category: product?.representedProduct?.c_materialGroup,
                        prdha_category: product?.c_prdha_category,
                        index: productIndex + 1,
                        stock_status:
                            product?.c_extendVariant?.stock > 0 ? 'in-stock' : 'out-of-stock',
                    },
                ],
            }),
        )
    }

    let inventorySize = null

    if ('c_extendVariant' in product && product.c_extendVariant) {
        inventorySize = product?.c_extendVariant?.stock
    } else if ('inventory' in product && product.inventory) {
        inventorySize = product?.inventory?.stockLevel
    }

    return (
        <Box
            {...styles.container}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onFocus={() => setIsHovered(true)}
            onBlur={() => setIsHovered(false)}
        >
            <Box data-testid="product-tile" {...rest} position="relative">
                <ProductTileImage
                    badgeDetails={badgeDetails}
                    dynamicImageProps={dynamicImageProps}
                    enableFavourite={enableFavourite}
                    isFavourite={isFavourite}
                    onFavouriteToggle={onFavouriteToggle}
                    product={product}
                    productUrl={productUrl}
                    compactView={compactView}
                    priceData={priceData}
                    isOfficina={product?.representedProduct?.c_isOfficina === 'true'}
                    variant={shouldHideVariants ? 'transparent' : ''}
                    selectItemTileEventGtm={selectItemTileEventGtm}
                    isFromHelpMeChoose={
                        shouldHideReviews && shouldHideVariants && shouldHideCompare
                    }
                    productIndex={productIndex}
                />
                <Box>
                    {inventorySize === 0 || inventorySize === null ? (
                        <Box {...styles.stockLabelContainer}>
                            <StockLabel
                                variant="outOfStock"
                                icon={<CloseIcon />}
                                textMessage={
                                    <FormattedMessage
                                        id="use_product.message.out_of_stock"
                                        defaultMessage="Out of stock"
                                    />
                                }
                            />
                        </Box>
                    ) : (
                        inventorySize > 0 &&
                        inventorySize <= lastItemThreshold && (
                            <Box {...styles.stockLabelContainer}>
                                <StockLabel
                                    variant="lastFewItems"
                                    icon={<InfoIcon circleColor="transparent" />}
                                    textMessage={
                                        <FormattedMessage
                                            id="use_product.message.low_stock"
                                            defaultMessage="Last {quantity} {itemLabel}"
                                            values={{
                                                quantity: inventorySize,
                                                itemLabel:
                                                    inventorySize === 1 ? (
                                                        <FormattedMessage
                                                            id="global.message.item"
                                                            defaultMessage="item"
                                                        />
                                                    ) : (
                                                        <FormattedMessage
                                                            id="global.message.items"
                                                            defaultMessage="items"
                                                        />
                                                    ),
                                            }}
                                        />
                                    }
                                />
                            </Box>
                        )
                    )}
                    <Text {...styles.category}>{product?.c_categoryName}</Text>
                    <Box {...styles.titlePriceWrapper} id="title-price-wrapper">
                        {/* Title */}
                        <Flex {...styles.titleWrapper} flexDirection={'column'}>
                            <Text {...styles.title}>{localizedProductName}</Text>
                            <Text
                                {...styles.sku}
                                className={
                                    shouldHideReviews && shouldHideVariants && shouldHideCompare
                                        ? 'del-hmc-model-number'
                                        : 'del-plp-model-number'
                                }
                            >
                                {product.productId || product?.c_subModelIdentifier}
                            </Text>
                        </Flex>
                        <Flex {...styles.priceWrapper} flexDirection={'column'}>
                            <DisplayPrice
                                priceData={priceData}
                                currency={currency}
                                listPriceProps={{...styles.listPrice}}
                                currentPriceProps={{...styles.currentPrice}}
                                showSuggestesLabel={true}
                            />
                            {/* Scenario 3 Omnibus: on PLP show the info-price component */}
                            {priceData.showOmnibusPriceDiscount && (
                                <OmnibusPrice
                                    priceData={priceData}
                                    currency={currency}
                                    omnibusExpandedView={false}
                                />
                            )}
                            {isVATFlag && (
                                <VatLabel
                                    price={priceData?.currentPrice}
                                    vatRate={priceData.taxRate}
                                    variant="tile"
                                />
                            )}
                        </Flex>
                    </Box>

                    <Box ref={errorContainerRef}>
                        {showErrorMessage ? (
                            <Fade in={true}>
                                <Text color="text.danger" fontWeight={600} marginBottom={8}>
                                    {showErrorMessage ===
                                    ADD_TO_CART_ERROR_KEYS.INVENTORY_REMAINING_FOR_PRODUCT
                                        ? formatMessage(
                                              ADD_TO_CART_ERROR_MESSAGES.INVENTORY_REMAINING_FOR_PRODUCT,
                                              {
                                                  stockLevel: inventorySize || 0,
                                                  productName: product.productName,
                                              },
                                          )
                                        : formatMessage(
                                              ADD_TO_CART_ERROR_MESSAGES.NO_VARIANT_SELECTED,
                                          )}
                                </Text>
                            </Fade>
                        ) : (
                            <></>
                        )}
                    </Box>
                    {/* Promotion call-out message */}
                    {/* 
                    {shouldShowPromoCallout(productWithFilteredVariants) && (
                        <PromoCallout product={productWithFilteredVariants} />
                    )} 
                    */}

                    {(product?.representedProduct?.c_energyEfficiencyClass ||
                        product?.c_energyEfficiencyClass) && (
                        <Box {...styles.energyLabel}>
                            <EnergyLabel
                                level={
                                    product?.representedProduct?.c_energyEfficiencyClass ||
                                    product?.c_energyEfficiencyClass
                                }
                                description={formatMessage({
                                    defaultMessage: 'Product information',
                                    id: 'energy_label.product_information',
                                })}
                                textWidth={compactView ? 'medium' : 'big'}
                                labelPosition="left"
                                manualDocument={product?.c_manualDocument}
                            />
                        </Box>
                    )}
                    <Box {...styles.reviewCompareWrapper}>
                        {!shouldHideReviews && (
                            <Box {...styles.reviews}>
                                <ReviewScoreIcon id={product.productId} />
                            </Box>
                        )}
                        {!shouldHideCompare && (
                            <Box {...styles.compare}>
                                <Checkbox
                                    isChecked={isChecked}
                                    isDisabled={!isChecked && compareItems.length === 3}
                                    onChange={() => onCheckboxChange(product)}
                                    _checked={{
                                        '& .chakra-checkbox__control': {boxShadow: 'none'},
                                    }}
                                >
                                    <FormattedMessage
                                        id="product_tile.compare.label"
                                        defaultMessage="Compare"
                                    />
                                </Checkbox>
                            </Box>
                        )}
                    </Box>
                </Box>

                {(!isDesktop || isHovered) && (
                    <Box {...styles.collapsableSection}>
                        {!shouldHideVariants && !compactView ? (
                            <ProductTileSwatches
                                colorVariantsWithImages={colorVariantsWithImages}
                                styles={styles}
                                product={product}
                                isCustomFieldSwatches={!!product?.c_swatches}
                            />
                        ) : (
                            !shouldHideVariants && labelElement
                        )}
                        <Divider {...styles.divider} sx={{marginBottom: remCalc(24)}} />
                        {keyFeaturesTitles.length > 0 && (
                            <UnorderedList {...styles.featureList}>
                                {keyFeaturesTitles?.map((title) => (
                                    <ListItem key={title} {...styles.featureListElement}>
                                        {title}
                                    </ListItem>
                                ))}
                            </UnorderedList>
                        )}

                        {product.representedProduct?.c_isOfficina === 'true' ? (
                            <Button
                                as="a"
                                href={`/${localeId}${productUrl}`}
                                variant={compactView ? 'primary-small' : 'primary'}
                                w="100%"
                            >
                                <FormattedMessage
                                    id="global.configurator"
                                    defaultMessage="Configurator"
                                />
                            </Button>
                        ) : (
                            <Box {...styles.buttonGroup}>
                                {!notShoppableCountry && inventorySize !== 0 ? (
                                    <AddToCartButton
                                        product={product}
                                        compactView={compactView}
                                        setShowErrorMessage={setShowErrorMessage}
                                        errorContainerRef={errorContainerRef}
                                        showUniversalFeedback={true}
                                        isFromHelpMeChoose={
                                            shouldHideReviews &&
                                            shouldHideVariants &&
                                            shouldHideCompare
                                        }
                                        productIndex={productIndex}
                                    />
                                ) : (
                                    <></>
                                )}
                                {inventorySize === 0 || inventorySize === null ? (
                                    <NotifyMeButton product={product} compactView={compactView} />
                                ) : (
                                    <></>
                                )}
                                <Link to={productUrl}>
                                    <Button
                                        variant={compactView ? 'secondary-small' : 'secondary'}
                                        w="100%"
                                        onClick={selectItemTileEventGtm}
                                    >
                                        <FormattedMessage
                                            id="product_tile.discover_more"
                                            defaultMessage="Discover more"
                                        />
                                    </Button>
                                </Link>
                            </Box>
                        )}
                    </Box>
                )}
            </Box>
            {isDesktop && !shouldHideVariants && labelElement}
        </Box>
    )
}

ProductTile.displayName = 'ProductTile'

ProductTile.propTypes = {
    /**
     * The product search hit that will be represented in this
     * component.
     */
    product: PropTypes.shape({
        currency: PropTypes.string,
        id: PropTypes.string,
        image: PropTypes.shape({
            alt: PropTypes.string,
            disBaseLink: PropTypes.string,
            link: PropTypes.string,
        }),
        imageGroups: PropTypes.array,
        price: PropTypes.number,
        priceRanges: PropTypes.array,
        tieredPrices: PropTypes.array,
        // `name` is present and localized when `product` is provided by a RecommendedProducts component
        // (from Shopper Products `getProducts` endpoint), but is not present when `product` is
        // provided by a ProductList component.
        // See: https://developer.salesforce.com/docs/commerce/commerce-api/references/shopper-products?meta=getProducts
        name: PropTypes.string,
        // `productName` is localized when provided by a ProductList component (from Shopper Search
        // `productSearch` endpoint), but is NOT localized when provided by a RecommendedProducts
        // component (from Einstein Recommendations `getRecommendations` endpoint).
        // See: https://developer.salesforce.com/docs/commerce/commerce-api/references/shopper-search?meta=productSearch
        // See: https://developer.salesforce.com/docs/commerce/einstein-api/references/einstein-api-quick-start-guide?meta=getRecommendations
        // Note: useEinstein() transforms snake_case property names from the API response to camelCase
        productName: PropTypes.string,
        c_defaultName: PropTypes.string,
        c_defaultCategoryName: PropTypes.string,
        c_taxRate: PropTypes.number,
        c_prdha_category: PropTypes.string,
        productId: PropTypes.string,
        productPromotions: PropTypes.array,
        representedProduct: PropTypes.object,
        productType: PropTypes.object,
        hitType: PropTypes.string,
        variationAttributes: PropTypes.array,
        variationGroups: PropTypes.array,
        variants: PropTypes.array,
        c_categoryName: PropTypes.string,
        c_extendVariant: PropTypes.object,
        inventory: PropTypes.object,
        c_subModelIdentifier: PropTypes.string,
        c_omnibusPrice: PropTypes.shape({
            omnibusPercentage: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        }),
        c_swatches: PropTypes.array,
        c_energyEfficiencyClass: PropTypes.string,
        c_manualDocument: PropTypes.string,
        type: PropTypes.shape({
            set: PropTypes.bool,
            bundle: PropTypes.bool,
            item: PropTypes.bool,
        }),
        c_variationGroupsUrl: PropTypes.array,
    }),
    productIndex: PropTypes.number,
    categoryName: PropTypes.string,
    /**
     * Props to hide collapsable sections & features (example: HelpMeChoose page)
     */
    shouldHideReviews: PropTypes.bool,
    shouldHideVariants: PropTypes.bool,
    shouldHideCompare: PropTypes.bool,
    /**
     * Enable adding/removing product as a favourite.
     * Use case: wishlist.
     */
    enableFavourite: PropTypes.bool,
    /**
     * Display the product in a compact view. defaults to false
     */
    gridView: PropTypes.bool,
    /**
     * Display the product as a favourite.
     */
    isFavourite: PropTypes.bool,
    /**
     * Callback function to be invoked when the user
     * interacts with favourite icon/button.
     */
    onFavouriteToggle: PropTypes.func,
    /**
     * When displaying a master/variant product, this value represents the variation attribute that is displayed
     * as a swatch below the main image. The default for this property is `color`.
     */
    selectableAttributeId: PropTypes.string,
    dynamicImageProps: PropTypes.object,
    /**
     * Details of badge labels and the corresponding product custom properties that enable badges.
     */
    badgeDetails: PropTypes.array,
}

export default ProductTile

/**
 * Component for rendering swatches of color variants for a product.
 *
 * @param {Object} props - The component props.
 * @param {Array} props.colorVariantsWithImages - Array of color variant objects, each containing information about the variant and its associated images.
 * @param {Object} props.styles - The style configuration object for the swatches.
 * @param {Object} props.product - The product object containing details about the current product.
 * @param {boolean} props.isCustomFieldSwatches - Flag indicating whether the swatches are derived from a custom field or fallback logic.
 *
 * @returns {JSX.Element} A component that renders swatches for color variants.
 */
const ProductTileSwatches = ({colorVariantsWithImages, styles, product, isCustomFieldSwatches}) => {
    const intl = useIntl()
    return (
        <Box
            {...styles.swatchContainer}
            role="radiogroup"
            aria-label={intl.formatMessage({
                id: 'product_tile.color_variants.aria_label',
                defaultMessage: 'Color variants',
            })}
        >
            {colorVariantsWithImages?.map((swatch, index) => {
                /*
                Fallback logic for tiles without swatches custom field
                */

                const image = isCustomFieldSwatches ? swatch : swatch.images?.[0]
                const swatchIdentifier = swatch?.subModelIdentifier ?? swatch?.productId
                const swatchIdentifierBuildProductUrl = isCustomFieldSwatches
                    ? swatch?.subModelIdentifier
                    : swatch?.variationValues?.subModelIdentifier

                if (isCustomFieldSwatches && swatch?.subModelIdentifier === product?.productId)
                    return

                let productUrl = product?.c_variationGroupsUrl?.find(
                    (item) => item.productId === swatchIdentifier,
                )
                if (!productUrl) {
                    productUrl = rebuildPathWithParams(productUrlBuilder(product), {
                        subModelIdentifier: swatchIdentifierBuildProductUrl,
                    })
                }

                return (
                    <Swatch
                        href={productUrl?.slugUrl}
                        key={swatchIdentifier}
                        value={swatchIdentifier}
                        name={'color'}
                        variant={'image'}
                        size="small"
                        position={index + 1}
                        total={colorVariantsWithImages?.length}
                        aria-label={swatch?.subModelIdentifier ?? swatch?.productId}
                        isFocusable
                    >
                        <Box {...styles.swatchThumbnailWrapper}>
                            <AspectRatio ratio={1} {...styles.swatchThumbnail}>
                                <Img alt={image?.alt} src={image?.link} />
                            </AspectRatio>
                            <Box {...styles.imageThumbnailOverlay} />
                        </Box>
                    </Swatch>
                )
            })}
        </Box>
    )
}

ProductTileSwatches.propTypes = {
    colorVariantsWithImages: PropTypes.array,
    styles: PropTypes.object,
    product: PropTypes.object,
    isCustomFieldSwatches: PropTypes.bool,
}
